import { init, captureException, captureMessage, setUser, withScope } from '@sentry/react';
import { browserTracingIntegration } from '@sentry/browser';
import logger from './logger';

class SentryManager {
    constructor() {
        this.initialized = false;
    }

    init() {
        if (this.initialized) {
            return;
        }
        const isProd = process.env.REACT_APP_STAGE === 'prod';
        const isSandbox = process.env.REACT_APP_STAGE === 'sandbox';
        // Only initialize in production or sandbox environments
        if (!isProd && !isSandbox) {
            return;
        }
        // Keep development API URL for testing
        const API_URL =
            process.env.NODE_ENV !== 'development' ? `${window.location.origin}/` : process.env.REACT_APP_API_URL;

        try {
            init({
                // Basic configuration
                dsn: process.env.REACT_APP_SENTRY_DSN,
                environment: process.env.REACT_APP_SENTRY_ENVIRONMENT,
                release: process.env.REACT_APP_SENTRY_RELEASE,
                // Performance monitoring
                integrations: [
                    browserTracingIntegration({
                        tracePropagationTargets: [window.location.origin, `${API_URL}api`],
                    }),
                ],
                // Performance Sampling
                tracesSampleRate: isProd ? 0.1 : 0,
                replaysSessionSampleRate: isProd ? 0.1 : 0,
                replaysOnErrorSampleRate: 1.0,
                // Log events before sending to Sentry
                beforeSend(event) {
                    if (event.exception) {
                        logger.error('[Sentry]', event.exception);
                    }
                    return event;
                },
                // Error filtering
                ignoreErrors: [
                    'Non-Error promise rejection captured',
                    'ResizeObserver loop limit exceeded',
                    /^Network request failed$/,
                    'Server returned handshake error: Handshake was canceled',
                ],
            });

            this.initialized = true;
            logger.success('Sentry initialized successfully');
        } catch (error) {
            logger.error('Failed to initialize Sentry:', error);
        }
    }

    /**
     * Captures and reports exceptions to Sentry
     * @param {Error} error - The error object to capture
     * @param {Object} context - Additional context for the error
     */
    captureException(error, context = {}) {
        if (!this.initialized) return;

        withScope(scope => {
            scope.setExtras(context);
            captureException(error);
        });
    }

    /**
     * Captures custom messages with specified severity levels
     * @param {string} message - Message to capture
     * @param {string} level - Severity level ('info', 'warning', 'error')
     * @param {Object} context - Additional context for the message
     */
    captureMessage(message, level = 'info', context = {}) {
        if (!this.initialized) return;

        withScope(scope => {
            scope.setLevel(level);
            scope.setExtras(context);
            captureMessage(message);
        });
    }

    setUser(user) {
        if (!this.initialized) return;
        setUser({ id: user.id, email: user.email });
    }

    clearUser() {
        if (!this.initialized) return;
        setUser(null);
    }
}

export default new SentryManager();
